import React, { useEffect } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import classNames from "classnames/bind";
import { Button, Input } from "antd";
import { Logo, IconMoney } from "../../../assets/svg/logo";
import { useRecruitment } from "../../../context/RecruitmentContext";
import styles from "./ChatBotRecruiment.module.sass";
import { hasRecruitmentSessionActive } from "../../../utils/helps";
import QuillEditor from "../../../components/QuillEditor/QuillEditor";
import HeaderChatCta from "../../../layouts/desktop/Header/HeaderChatCta";
import CampaignChatPanel from "./components/CampaignChatPanel";

const cx = classNames.bind(styles);

const PageHeader = ({ onOpenChat }) => {
  const navigate = useNavigate();
  const { searchInput, setSearchInput, handleSearch, isCandidateRegistered } =
    useRecruitment();

  return (
    <header className={cx("pageHeader")}>
      <div className={cx("headerBrand")} onClick={() => navigate("/")}>
        <div className={cx("logoBox")}>
          <Logo />
        </div>
        <div className={cx("headerTitle")}>
          <h1>Trợ lý tuyển dụng AI</h1>
          <p>Tư vấn vị trí phù hợp và hỗ trợ tuyển nhanh</p>
        </div>
      </div>
      <div className={cx("headerActions")}>
        {!isCandidateRegistered && <HeaderChatCta onClick={onOpenChat} />}
        <div className={cx("headerSearch")}>
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Tìm kiếm vị trí"
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            allowClear
          />

          <Button type="primary" onClick={handleSearch}>
            Tìm kiếm
          </Button>
        </div>
      </div>
    </header>
  );
};

const SIDEBAR_META_FIELDS = [
  { label: "Địa điểm", key: "location" },
  { label: "Số lượng cần tuyển", key: "quantity" },
  { label: "Kinh nghiệm", key: "experience" },
  { label: "Trình độ học vấn", key: "education" },
];

const SidebarJobCard = ({ campaign, active, onClick }) => (
  <button
    type="button"
    className={cx("sidebarJobCard", { active })}
    onClick={onClick}
  >
    <h3 className={cx("sidebarJobTitle")}>{campaign.title}</h3>
    <p className={cx("sidebarDepartment")}>{campaign.department}</p>

    <div className={cx("sidebarMetaList")}>
      {SIDEBAR_META_FIELDS.map((field) => (
        <div key={field.key} className={cx("sidebarMetaLine")}>
          <span>{field.label}</span>
          <strong>{campaign[field.key] ?? "—"}</strong>
        </div>
      ))}
    </div>

    <div className={cx("salaryBadgeGreen", "sidebarSalaryBadge")}>
      <IconMoney />
      <span>{campaign.salaryRange}</span>
    </div>
  </button>
);

const DetailSection = ({ icon, title, content, values }) => (
  <div className={cx("detailSection")}>
    <h3>
      <span className={cx("sectionIcon")}>{icon}</span>
      {title}
    </h3>

    {content ? (
      <QuillEditor value={content} className={cx("detailQuill")} />
    ) : (
      <ul>
        {values?.map((item, index) => (
          <li key={`${title}-${index}`}>{item}</li>
        ))}
      </ul>
    )}
  </div>
);

const DetailPanel = ({ campaign, onApply }) => (
  <div className={cx("detailPanel")}>
    <div className={cx("detailHeader")}>
      <div className={cx("detailHeaderInfo")}>
        <span className={cx("detailTitle")}>{campaign.title}</span>

        <p className={cx("departmentLabel")}>{campaign.department}</p>

        <div className={cx("detailMetaBar")}>
          <span className={cx("detailMetaItem")}>
            <em>Số lượng cần tuyển</em> {campaign.quantity}
          </span>

          <span className={cx("metaDivider")} aria-hidden="true">
            |
          </span>

          <span className={cx("detailMetaItem")}>
            <em>Kinh nghiệm</em> {campaign.experience}
          </span>

          <span className={cx("metaDivider")} aria-hidden="true">
            |
          </span>

          <span className={cx("detailMetaItem")}>
            <em>Học vấn</em> {campaign.education}
          </span>
        </div>
      </div>

      <button type="button" className={cx("applyButton")} onClick={onApply}>
        <span className={cx("applyIcon")}>📄</span>
        Ứng tuyển ngay
      </button>
    </div>

    <div className={cx("detailContent")}>
      <DetailSection
        icon="💼"
        title="Mô tả công việc"
        content={campaign.description}
      />

      <DetailSection
        icon="📋"
        title="Yêu cầu ứng viên"
        content={campaign.requirements}
      />

      <DetailSection icon="🎁" title="Quyền lợi" content={campaign.benefits} />

      <DetailSection
        icon="🕐"
        title="Thời gian làm việc"
        content={campaign.workingTime}
      />
    </div>
  </div>
);

const GeneralDetailPanel = ({ onOpenChat }) => (
  <div className={cx("detailPanel")}>
    <div className={cx("detailHeader")}>
      <div className={cx("detailHeaderInfo")}>
        <span className={cx("detailTitle")}>Trợ lý tuyển dụng AI</span>

        <p className={cx("departmentLabel")}>Trò chuyện chung</p>

        <div className={cx("detailMetaBar")}>
          <span className={cx("detailMetaItem")}>
            <em>Chế độ</em> Hỏi đáp chung
          </span>
        </div>
      </div>

      <button type="button" className={cx("applyButton")} onClick={onOpenChat}>
        <span className={cx("applyIcon")}>💬</span>
        Bắt đầu trò chuyện
      </button>
    </div>

    <div className={cx("detailContent")}>
      <DetailSection
        icon="✨"
        title="Hỗ trợ chung"
        values={[
          "Hỏi về quy trình tuyển dụng",
          "Hỏi về hồ sơ, CV và phỏng vấn",
          "Tìm hiểu các vị trí đang tuyển",
        ]}
      />
    </div>
  </div>
);

const ChatTabs = ({
  getCampaignById,
  openCampaignIds,
  activeCampaignId,
  onActivate,
  onClose,
}) => {
  if (openCampaignIds.length === 0) return null;

  return (
    <div className={cx("chatTabsBar")} aria-label="Các cuộc trò chuyện đã mở">
      <div className={cx("chatTabsList")}>
        {openCampaignIds.map((id) => {
          const tabCampaign = getCampaignById(id);
          const isActive = String(id) === String(activeCampaignId);

          return (
            <div key={id} className={cx("chatTab", { active: isActive })}>
              <button
                type="button"
                className={cx("chatTabButton")}
                onClick={() => onActivate(id)}
              >
                <span className={cx("chatTabTitle")}>
                  {tabCampaign?.title || `JD ${id}`}
                </span>
              </button>

              <button
                type="button"
                className={cx("chatTabClose")}
                aria-label={`Đóng tab ${tabCampaign?.title || id}`}
                onClick={() => onClose(id)}
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const GENERAL_CHAT_KEY = "__general__";

const CampaignDetailPage = () => {
  const { campaignId } = useParams();
  const location = useLocation();

  const navigate = useNavigate();

  const {
    campaigns,
    getCampaignById,
    loading,
    chatStarted,
    setChatStarted,
    isCandidateRegistered,
    openCandidateModal,
    requestChatAccess,
    openChatCampaignIds,
    activeChatCampaignId,
    closeChatCampaign,
    activateChatCampaign,
    clearSearch,
    selectChatCampaign,
  } = useRecruitment();

  const campaign = getCampaignById(campaignId);
  const activeChatCampaign =
    getCampaignById(activeChatCampaignId) || campaign;

  useEffect(() => {
    if (!isCandidateRegistered || !campaignId) return;
    activateChatCampaign(campaignId);
    setChatStarted(true);
  }, [campaignId, isCandidateRegistered, activateChatCampaign, setChatStarted]);

  const isChatRoute =
    location.pathname === "/chat" || location.pathname.endsWith("/chat");
  const hasStoredChatSession = campaignId
    ? hasRecruitmentSessionActive(campaignId)
    : hasRecruitmentSessionActive(GENERAL_CHAT_KEY);

  const showChat =
    chatStarted &&
    isCandidateRegistered &&
    (isChatRoute ||
      !campaignId ||
      openChatCampaignIds.length > 0 ||
      hasStoredChatSession);

  const handleOpenChat = () => {
    requestChatAccess(campaignId);
  };

  const handleApply = () => {
    if (!isCandidateRegistered) {
      openCandidateModal(campaignId, { showCvUpload: true });
      return;
    }
    requestChatAccess(campaignId);
  };

  const handleSelectCampaign = (id) => {
    if (!isCandidateRegistered) {
      clearSearch();
      navigate(`/${id}`);
      return;
    }

    if (selectChatCampaign(id)) {
      navigate(`/${id}`);
    }
  };

  const handleActivateChatTab = (id) => {
    activateChatCampaign(id);
    clearSearch();
    navigate(`/${id}`);
  };

  const handleCloseChatTab = (id) => {
    const wasActive = String(id) === String(activeChatCampaignId);
    const nextActiveId = closeChatCampaign(id);

    if (!nextActiveId) {
      activateChatCampaign(null);
      navigate("/chat");
      return;
    }

    if (wasActive) {
      navigate(`/${nextActiveId}`);
    }
  };

  if (!loading && campaignId && !campaign && campaigns.length > 0) {
    return <Navigate to={`/${campaigns[0].id}`} replace />;
  }

  if (loading) {
    return (
      <>
        <PageHeader onOpenChat={handleOpenChat} />

        <div className={cx("emptyList")}>
          <p>Đang tải thông tin vị trí...</p>
        </div>
      </>
    );
  }

  return (
    <div className={cx("detailPage")}>
      <PageHeader onOpenChat={handleOpenChat} />

      <div className={cx("splitBody", { splitBodyChat: showChat })}>
        <aside className={cx("detailSidebar")}>
          <div className={cx("campaignList")}>
            {campaigns.map((item) => (
              <SidebarJobCard
                key={item.id}
                campaign={item}
                active={String(item.id) === String(campaignId)}
                onClick={() => handleSelectCampaign(item.id)}
              />
            ))}
          </div>
        </aside>

        <main
          className={cx("content", "detailMain", "workspaceMain", {
            workspaceMainChat: showChat,
          })}
        >
          {showChat ? (
            <>
              <ChatTabs
                getCampaignById={getCampaignById}
                openCampaignIds={openChatCampaignIds}
                activeCampaignId={activeChatCampaignId}
                onActivate={handleActivateChatTab}
                onClose={handleCloseChatTab}
              />
              <CampaignChatPanel
                key={activeChatCampaignId ?? campaign?.id ?? "general"}
                campaign={activeChatCampaign}
                onApply={handleApply}
                showApplyButton={isCandidateRegistered}
              />
            </>
          ) : !campaign ? (
            <GeneralDetailPanel onOpenChat={handleOpenChat} />
          ) : (
            <DetailPanel campaign={campaign} onApply={handleApply} />
          )}
        </main>
      </div>
    </div>
  );
};

export default CampaignDetailPage;
